import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import './ThreeBackground.css';

const ThreeBackground = () => {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);

    // Create particles
    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 2000;
    const posArray = new Float32Array(particlesCount * 3);

    for (let i = 0; i < particlesCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 20;
    }

    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));

    // Material
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.05,
      color: 0xffffff,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });

    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particlesMesh);

    // Create wireframe geometry
    const geometry = new THREE.IcosahedronGeometry(2, 1);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      wireframe: true,
      transparent: true,
      opacity: 0.3
    });
    const wireframe = new THREE.Mesh(geometry, material);
    scene.add(wireframe);

    // Add more wireframe objects
    const geometry2 = new THREE.OctahedronGeometry(1.5, 0);
    const material2 = new THREE.MeshBasicMaterial({
      color: 0xb0b0b0,
      wireframe: true,
      transparent: true,
      opacity: 0.2
    });
    const wireframe2 = new THREE.Mesh(geometry2, material2);
    scene.add(wireframe2);

    // Mouse tracking
    const mouse = { x: 0, y: 0 };
    const targetMouse = { x: 0, y: 0 };

    const handleMouseMove = (event) => {
      targetMouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      targetMouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Animation
    const clock = new THREE.Clock();
    let time = 0;

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      time = clock.getElapsedTime();

      // Smooth mouse tracking
      mouse.x += (targetMouse.x - mouse.x) * 0.05;
      mouse.y += (targetMouse.y - mouse.y) * 0.05;

      // Rotate wireframes with mouse influence
      wireframe.rotation.x = time * 0.2 + mouse.y * 0.5;
      wireframe.rotation.y = time * 0.3 + mouse.x * 0.5;
      wireframe2.rotation.x = -time * 0.15 + mouse.y * 0.3;
      wireframe2.rotation.y = -time * 0.25 + mouse.x * 0.3;

      // Animate particles
      const positions = particlesGeometry.attributes.position.array;
      for (let i = 0; i < particlesCount; i++) {
        const i3 = i * 3;
        positions[i3 + 1] += Math.sin(time + i) * 0.001;
        if (positions[i3 + 1] > 10) positions[i3 + 1] = -10;
      }
      particlesGeometry.attributes.position.needsUpdate = true;

      // Camera movement with mouse interaction
      camera.position.x = Math.sin(time * 0.1) * 0.5 + mouse.x * 2;
      camera.position.y = Math.cos(time * 0.1) * 0.5 + mouse.y * 2;
      camera.position.z = 5;
      camera.lookAt(scene.position);

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    sceneRef.current = scene;

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      renderer.dispose();
      particlesGeometry.dispose();
      particlesMaterial.dispose();
      geometry.dispose();
      material.dispose();
      geometry2.dispose();
      material2.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="three-canvas"
      data-engine="three.js"
    />
  );
};

export default ThreeBackground;

